import { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { ShieldCheck, Check, Smartphone, CreditCard, Building2, Lock, Globe } from "lucide-react";
import { Screen, TopBar, Logo, BackButton, ScrollArea, BottomBar, PrimaryButton } from "@/components/jk/ui";
import { Card, SectionLabel, ProgressBar } from "@/components/jk/kit";
import { formatINR, formatDate, formatTime } from "@/lib/format";
import { loadRazorpay } from "@/lib/razorpay";
import api from "@/lib/api";

const INR_METHODS = [
  { id: "upi", label: "UPI", sub: "GPay · PhonePe · Paytm", icon: Smartphone },
  { id: "card", label: "Card", sub: "Credit / Debit", icon: CreditCard },
  { id: "netbanking", label: "Net Banking", sub: "All major banks", icon: Building2 },
];

const Payment = () => {
  const navigate = useNavigate();
  const { slug } = useParams();
  const { state } = useLocation();
  const [method, setMethod] = useState("upi");
  const [opts, setOpts] = useState(null);
  const [intlCurrency, setIntlCurrency] = useState(null);
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState("");
  const [fxAmounts, setFxAmounts] = useState(null);

  const bookingId = state?.bookingId;

  useEffect(() => {
    if (!bookingId) return;
    api.get(`/public/bookings/${bookingId}/payment-options`).then(({ data }) => {
      setOpts(data);
      if (data.multicurrency_enabled && data.options.length) {
        setIntlCurrency(data.options[0].currency);
        // Geo is a default-emphasis hint ONLY — never blocks INR.
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
        if (!/Kolkata|Calcutta/i.test(tz)) setMethod("intl");
      }
    }).catch(() => { /* fail-safe: INR-only */ });
  }, [bookingId]);

  useEffect(() => {
    if (!bookingId || state?.sel?.tier !== 3 || !state?.amount) return;
    Promise.all([
      api.get("/public/fx-display", { params: { amount_inr: state.amount, currency: "GBP" } }),
      api.get("/public/fx-display", { params: { amount_inr: state.amount, currency: "USD" } }),
      api.get("/public/fx-display", { params: { amount_inr: state.amount, currency: "EUR" } }),
    ]).then(([gbp, usd, eur]) => {
      setFxAmounts({ GBP: gbp.data.display_amount, USD: usd.data.display_amount, EUR: eur.data.display_amount });
    }).catch(() => {});
  }, [bookingId, state]);

  if (!bookingId) { navigate(`/${slug}`, { replace: true }); return null; }
  const { amount, serviceName, sel } = state;

  const intlEnabled = !!opts?.multicurrency_enabled && (opts?.options?.length || 0) > 0;
  const intlOpt = opts?.options?.find((o) => o.currency === intlCurrency) || null;
  const isIntl = method === "intl" && intlEnabled;
  const stripeTestMode = opts?.stripe_mode === "test";
  const totalLabel = isIntl && intlOpt ? `${intlOpt.symbol}${intlOpt.display_amount}` : formatINR(amount);

  const goConfirmed = () => navigate(`/booking/${bookingId}/done`, { replace: true });

  const pay = async () => {
    setPaying(true);
    setError("");
    if (isIntl) {
      try {
        const { data } = await api.post(`/public/bookings/${bookingId}/stripe-checkout`, {
          currency: intlCurrency, origin_url: window.location.origin,
        });
        window.location.href = data.url; // redirect to Stripe hosted checkout
      } catch (e) {
        setError(e?.response?.data?.detail || "Could not start card payment. You can pay in INR instead.");
        setPaying(false);
      }
      return;
    }
    try {
      const { data } = await api.post(`/public/bookings/${bookingId}/create-order`);
      if (data.mock) {
        await api.post(`/public/bookings/${bookingId}/pay`);
        return goConfirmed();
      }
      const ok = await loadRazorpay();
      if (!ok) { setError("Could not load the payment gateway. Check your connection."); setPaying(false); return; }
      const rzp = new window.Razorpay({
        key: data.key_id,
        order_id: data.order_id,
        amount: data.amount,
        currency: data.currency,
        name: "JothidaKural",
        description: serviceName,
        theme: { color: "#111111" },
        handler: async (resp) => {
          try {
            await api.post(`/public/bookings/${bookingId}/verify-payment`, {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            goConfirmed();
          } catch {
            setError("Payment could not be verified. If money was deducted, it will be refunded.");
            setPaying(false);
          }
        },
        modal: { ondismiss: () => setPaying(false) },
      });
      rzp.on("payment.failed", () => { setError("Payment failed. Please try again."); setPaying(false); });
      rzp.open();
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not start payment.");
      setPaying(false);
    }
  };

  return (
    <Screen testid="payment-screen">
      <TopBar left={<BackButton onClick={() => navigate(-1)} />} center={<Logo className="h-[16px] w-auto" />} />
      <div className="px-6 pt-1"><ProgressBar value={100} /><p className="mt-2 text-[12px] text-[#666666]">Step 2 of 2</p></div>

      <ScrollArea className="px-6 pt-4">
        <h1 className="text-[26px] font-bold tracking-[-0.02em] text-[#111111]">Payment</h1>

        <Card className="mt-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[15px] font-semibold text-[#111111]">{serviceName}</p>
              {sel && <p className="mt-0.5 text-[12px] text-[#666666]">{formatDate(sel.date)} · {formatTime(sel.time)}</p>}
            </div>
          </div>
          <div className="mt-4 flex items-center justify-between border-t border-[#f0ede8] pt-4">
            <span className="text-[14px] text-[#666666]">Total payable</span>
            <span className="text-[22px] font-bold text-[#111111]" data-testid="total-amount">{totalLabel}</span>
          </div>
        </Card>

        {/* Subtle INR-vs-card comparison (UPI is the cheapest rail) */}
        {intlEnabled && intlOpt && (
          <p className="mt-3 text-center text-[12.5px] text-[#777]" data-testid="price-compare">
            Pay <span className="font-semibold text-[#111]">{formatINR(opts.inr_amount)}</span> via UPI
            <span className="text-[#1a8f4c]"> (cheapest)</span> · or <span className="font-semibold text-[#111]">{intlOpt.symbol}{intlOpt.display_amount}</span> by card
          </p>
        )}

        <div className="mt-6">
          <SectionLabel className="mb-3">Payment method</SectionLabel>
          <div className="space-y-2.5" data-testid="payment-methods">
            {INR_METHODS.map((m) => {
              const Icon = m.icon;
              const on = method === m.id;
              return (
                <button key={m.id} data-testid={`method-${m.id}`} onClick={() => setMethod(m.id)}
                  className={`flex w-full items-center gap-3 rounded-[14px] border p-4 text-left transition-colors ${on ? "border-[#111111] bg-white" : "border-[#e9e6e1] bg-white"}`}>
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#f2f0ec] text-[#111111]"><Icon size={18} strokeWidth={1.75} /></span>
                  <div className="flex-1"><p className="text-[14px] font-semibold text-[#111111]">{m.label}</p><p className="text-[12px] text-[#666666]">{m.sub}</p></div>
                  <span className={`flex h-5 w-5 items-center justify-center rounded-full border-2 ${on ? "border-[#111111] bg-[#111111]" : "border-[#cfccc6]"}`}>{on && <Check size={12} strokeWidth={3} className="text-white" />}</span>
                </button>
              );
            })}

            {intlEnabled && (
              <div data-testid="method-intl" role="button" tabIndex={0} onClick={() => setMethod("intl")}
                className={`w-full cursor-pointer rounded-[14px] border p-4 text-left transition-colors ${isIntl ? "border-[#111111] bg-white" : "border-[#e9e6e1] bg-white"}`}>
                <div className="flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#f2f0ec] text-[#111111]"><Globe size={18} strokeWidth={1.75} /></span>
                  <div className="flex-1">
                    <p className="text-[14px] font-semibold text-[#111111]">International card</p>
                    <p className="text-[12px] text-[#666666]">Pay in {opts.options.map((o) => o.currency).join(" / ")}</p>
                  </div>
                  <span className={`flex h-5 w-5 items-center justify-center rounded-full border-2 ${isIntl ? "border-[#111111] bg-[#111111]" : "border-[#cfccc6]"}`}>{isIntl && <Check size={12} strokeWidth={3} className="text-white" />}</span>
                </div>
                {isIntl && (
                  <div className="mt-3 flex gap-2 pl-[52px]" data-testid="intl-currencies">
                    {opts.options.map((o) => (
                      <span key={o.currency} role="button" tabIndex={0} data-testid={`intl-currency-${o.currency}`}
                        onClick={(e) => { e.stopPropagation(); setIntlCurrency(o.currency); }}
                        className={`rounded-[10px] border px-3 py-1.5 text-[13px] font-medium transition-colors ${intlCurrency === o.currency ? "border-[#111] bg-[#111] text-white" : "border-[#e0ddd7] text-[#555]"}`}>
                        {o.symbol}{o.display_amount} {o.currency}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="mt-5 flex items-center gap-3 rounded-[14px] bg-[var(--jk-periwinkle)] p-4" data-testid="payment-secure">
          <ShieldCheck size={20} strokeWidth={1.75} className="shrink-0 text-[#111111]" />
          {isIntl ? (
            stripeTestMode ? (
              <p className="text-[12px] leading-relaxed text-[#444]"><span className="font-semibold text-[#111111]">Secured by Stripe.</span> Test mode — use card <span className="font-medium">4242 4242 4242 4242</span>, any future expiry & CVC.</p>
            ) : (
              <p className="text-[12px] leading-relaxed text-[#444]"><span className="font-semibold text-[#111111]">Secured by Stripe.</span> Your card will be charged in {intlCurrency}. Payments are processed securely by Stripe.</p>
            )
          ) : (
            <p className="text-[12px] leading-relaxed text-[#444]"><span className="font-semibold text-[#111111]">Secured by Razorpay.</span> Test mode — use UPI <span className="font-medium">success@razorpay</span> or card <span className="font-medium">4111 1111 1111 1111</span>.</p>
          )}
        </div>
      </ScrollArea>

      <BottomBar>
        {error && <p className="mb-3 text-center text-[13px] text-[#d23b2f]" data-testid="payment-error">{error}</p>}
        <PrimaryButton testid="pay-button" onClick={pay} disabled={paying} icon={!paying && <Lock size={15} strokeWidth={2} />}>
          {paying ? "Processing…" : `Pay ${totalLabel}`}
        </PrimaryButton>
      </BottomBar>
    </Screen>
  );
};

export default Payment;
